import React from 'react';
import { Button, Dialog, Modal } from 'alurkerja-ui';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import { InputField } from '@/components/form/fields/InputField';
import { IModuleSchema, moduleSchema, ModuleService } from '@/api/module';
import { zodResolver } from '@hookform/resolvers/zod';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { FaRegClone } from 'react-icons/fa6';
import { FaRegCopy } from 'react-icons/fa';
import { LinkButton } from '@/components/shared/button/LinkButton';

type CloneModuleProps = {
  onSuccess: () => void;
  module?: IModuleSchema;
  triggerButtonType?: 'list' | 'module';
};

interface ModalContentProps extends CloneModuleProps {
  closeModal?: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  onSuccess,
  module,
}) => {
  const form = useForm<IModuleSchema>({
    resolver: zodResolver(moduleSchema),
    defaultValues: {
      ...module,
      module_name: `${module?.module_name}`,
    },
  });

  const { mutate: clone, isPending: isLoading } = ModuleService.useClone();

  const submitAction = form.handleSubmit(data => {
    if (!module?.id) return;

    clone(
      { id: module.id, payload: { module_name: data.module_name } },
      {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Success duplicate',
            callback: () => {
              onSuccess();
              closeModal?.();
            },
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Error',
            description: 'Failed to duplicate',
          });
        },
      },
    );
  });

  return (
    <FormContainer form={form}>
      <InputField
        label={`Parent ${module?.submodule?.length ? 'Epic' : 'User Story'}`}
        name="module_name"
        className="py-4"
      />

      <FormFooter>
        <OutlineButton onClick={closeModal}>Cancel</OutlineButton>
        <Button loading={isLoading} onClick={submitAction}>Duplicate</Button>
      </FormFooter>
    </FormContainer>
  );
};

export const CloneModule: React.FC<CloneModuleProps> = props => {
  const { triggerButtonType } = props;

  const triggerButton =
    triggerButtonType === 'list' ? (
      <Button
        variant="filled"
        icon={<FaRegClone size={18} className="text-white" />}
        type="button"
        className="bg-green-600"
      >
        Clone {props.module?.submodule?.length ? 'Epic' : 'User Story'}
      </Button>
    ) : (
      <LinkButton icon={<FaRegCopy className="text-green-600" />} />
    );

  return (
    <Modal
      title={`Duplicate ${props.module?.submodule?.length ? 'Epic' : 'User Story'}`}
      triggerButton={triggerButton}
    >
      {({ closeModal }) => (
        <div className="p-4">
          <ModalContent closeModal={closeModal} {...props} />
        </div>
      )}
    </Modal>
  );
};
